interface LocalSpeech {
  processLocally: boolean;
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start(): void;
  abort(): void;
}
type LocalSpeechConstructor = {
  new (): LocalSpeech;
  available(options: { langs: string[]; processLocally: true }): Promise<string>;
};
/** Host-authorized assistant. Model output is data, never code or HTML. */
export function safeReportPath(value: unknown, origin: string): string | null {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
  try {
    const url = new URL(value, origin);
    if (
      url.origin !== origin ||
      url.hash ||
      !/^\/reports\/(by_client|by_project|by_person)$/.test(url.pathname)
    )
      return null;
    return url.pathname + url.search;
  } catch {
    return null;
  }
}

export function mountAssistant(panel: HTMLElement, endpoint?: string) {
  let disposed = false;
  let request: AbortController | null = null;
  let enabled = false;
  let asking = false;
  let sessionScope: string | null = null;
  let speech: LocalSpeech | null = null;
  const cancel = () => {
    request?.abort();
    request = null;
    speech?.abort();
    speech = null;
  };
  const header = panel.querySelector('.panel-header');
  const title = panel.querySelector('.panel-title');
  const body = panel.querySelector<HTMLElement>('.panel-body');
  if (endpoint !== '/oncatch/assistant' || !header || !body || !title) {
    return { cancel, destroy: cancel };
  }
  const panelTitle = title;
  const panelBody = body;
  const originalTitle = title.textContent;
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'oc-assistant-toggle';
  toggle.setAttribute('role', 'switch');
  toggle.setAttribute('aria-label', 'Ask OnCatch');
  toggle.setAttribute('aria-checked', 'false');
  toggle.hidden = true;
  const thumb = document.createElement('span');
  toggle.append(thumb);
  title.after(toggle);
  const view = document.createElement('section');
  view.className = 'oc-assistant';
  view.hidden = true;
  const messages = document.createElement('div');
  messages.className = 'oc-assistant-messages';
  messages.setAttribute('role', 'log');
  messages.setAttribute('aria-live', 'polite');
  messages.setAttribute('aria-label', 'Conversation');
  const form = document.createElement('form');
  form.className = 'oc-assistant-compose';
  const input = document.createElement('textarea');
  input.setAttribute('aria-label', 'Message OnCatch');
  input.maxLength = 2000;
  input.rows = 3;
  const send = document.createElement('button');
  send.type = 'submit';
  send.textContent = 'Send';
  const stop = document.createElement('button');
  stop.type = 'button';
  stop.textContent = 'Cancel';
  stop.hidden = true;
  stop.addEventListener('click', () => {
    cancel();
    busy(false);
  });
  const speak = document.createElement('button');
  speak.type = 'button';
  speak.textContent = 'Speak';
  speak.hidden = true;
  // Never fall back to browser/cloud recognition. Only an already available
  // on-device language is offered; this does not install or download a pack.
  const speechClass = (window as unknown as { SpeechRecognition?: LocalSpeechConstructor })
    .SpeechRecognition;
  const language = document.documentElement.lang || 'en-US';
  if (speechClass && typeof speechClass.available === 'function') {
    void speechClass
      .available({ langs: [language], processLocally: true })
      .then((status) => {
        if (!disposed && status === 'available') speak.hidden = false;
      })
      .catch(() => {
        /* Intentionally no side effects. */
      });
    speak.addEventListener('click', () => {
      if (speech) {
        speech.abort();
        speech = null;
        speak.textContent = 'Speak';
        return;
      }
      const recognizer = new speechClass();
      recognizer.processLocally = true;
      recognizer.lang = language;
      recognizer.continuous = false;
      recognizer.interimResults = false;
      recognizer.onresult = (event) => {
        if (disposed || speech !== recognizer || !asking) return;
        input.value = `${input.value} ${Array.from(event.results)
          .map((result) => result[0]?.transcript || '')
          .join(' ')}`
          .trim()
          .slice(0, 2000);
      };
      recognizer.onend = recognizer.onerror = () => {
        if (speech === recognizer) speech = null;
        speak.textContent = 'Speak';
      };
      speech = recognizer;
      try {
        recognizer.start();
        speak.textContent = 'Stop speaking';
      } catch {
        speech = null;
        speak.textContent = 'Speak';
      }
    });
  }
  form.append(input, speak, send, stop);
  view.append(messages, form);
  body.after(view);
  function mode(next: boolean) {
    cancel();
    busy(false);
    asking = next;
    toggle.setAttribute('aria-checked', String(next));
    panelTitle.textContent = next ? 'Ask OnCatch' : originalTitle;
    panelBody.hidden = next;
    view.hidden = !next;
    if (next) input.focus();
  }
  toggle.addEventListener('click', () => {
    if (enabled) mode(!asking);
  });
  function busy(value: boolean) {
    form.setAttribute('aria-busy', String(value));
    speak.disabled = value;
    if (!value && !speech) speak.textContent = 'Speak';
    send.disabled = value;
    stop.hidden = !value;
    input.readOnly = value;
  }
  function message(text: string, user = false) {
    const node = document.createElement('p');
    node.className = user ? 'oc-assistant-user' : 'oc-assistant-response';
    node.textContent = text;
    messages.append(node);
    node.scrollIntoView?.({ block: 'nearest' });
  }
  async function json(response: Response) {
    if (response.redirected || !response.headers.get('content-type')?.includes('application/json'))
      throw new Error('Sign in to use Ask OnCatch.');
    if (!response.ok)
      throw new Error(
        response.status === 429
          ? 'Usage limit reached. Please try again later.'
          : 'Ask OnCatch is unavailable. Please try again.',
      );
    const text = await response.text();
    if (text.length > 12000) throw new Error('Unexpected assistant response.');
    return JSON.parse(text);
  }
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const prompt = input.value.trim();
    if (!enabled || !asking || !prompt || request) return;
    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
    if (!csrf) {
      message('Sign in to use Ask OnCatch.');
      return;
    }
    speech?.abort();
    speech = null;
    const controller = new AbortController();
    request = controller;
    message(prompt, true);
    busy(true);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        redirect: 'error',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrf,
        },
        body: JSON.stringify({ prompt }),
      });
      if (response.status === 401 || response.status === 403) {
        enabled = false;
        loaded = false;
        mode(false);
        toggle.hidden = true;
        return;
      }
      const result = await json(response);
      if (disposed || controller.signal.aborted) return;
      if (result.kind === 'report') {
        const path = safeReportPath(result.url, window.location.origin);
        if (!path) throw new Error('The requested page is not allowed.');
        window.location.assign(path);
      } else if (
        result.kind === 'feedback' &&
        typeof result.text === 'string' &&
        result.text === prompt
      ) {
        mode(false);
        const summary = body?.querySelector<HTMLTextAreaElement>('textarea[name="summary"]');
        const heading = body?.querySelector<HTMLInputElement>('input[name="title"]');
        if (summary) {
          summary.value =
            summary.value && summary.value !== prompt ? `${summary.value}\n\n${prompt}` : prompt;
          summary.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (heading && !heading.value) {
          heading.value = prompt.slice(0, 200);
          heading.dispatchEvent(new Event('input', { bubbles: true }));
        }
        heading?.focus();
      } else if (
        ['answer', 'clarify', 'rejected'].includes(result.kind) &&
        typeof result.message === 'string' &&
        result.message.length <= 6000
      ) {
        message(result.message);
      } else throw new Error('Unexpected assistant response.');
      input.value = '';
    } catch (error) {
      if (!controller.signal.aborted && !disposed)
        message(error instanceof Error ? error.message : 'Ask OnCatch is unavailable.');
    } finally {
      if (request === controller) {
        request = null;
        busy(false);
      }
    }
  });
  // Observe visibility so configuration is never requested on page load.
  let loaded = false;
  function load() {
    if (disposed || panel.hidden || loaded || request) return;
    const controller = new AbortController();
    request = controller;
    void fetch('/oncatch/assistant', {
      credentials: 'same-origin',
      redirect: 'error',
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
      .then(json)
      .then((config) => {
        if (!disposed && !controller.signal.aborted) {
          loaded = true;
          const scope =
            typeof config.sessionScope === 'string' && /^[a-f0-9]{64}$/.test(config.sessionScope)
              ? config.sessionScope
              : null;
          if (sessionScope !== scope) {
            messages.replaceChildren();
            input.value = '';
            mode(false);
          }
          sessionScope = scope;
          enabled = config.enabled === true && scope !== null;
          toggle.hidden = !enabled;
        }
      })
      .catch(() => {
        /* Intentionally no side effects. */
      })
      .finally(() => {
        if (request === controller) request = null;
      });
  }
  const visibility = new MutationObserver(() => {
    if (panel.hidden) {
      loaded = false;
      cancel();
      busy(false);
    } else load();
  });
  visibility.observe(panel, { attributes: true, attributeFilter: ['hidden'] });
  load();
  return {
    cancel: () => {
      cancel();
      busy(false);
    },
    destroy: () => {
      disposed = true;
      visibility.disconnect();
      cancel();
      toggle.remove();
      view.remove();
    },
  };
}
